import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Question, Category } from './types';
import { Checkbox } from '@/components/ui/checkbox';
import { ImageUpload } from './ImageUpload';

interface EditQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onEditQuestion: (data: Partial<Question>) => Promise<void>;
  question: Question;
  categories: Category[];
}

export const EditQuestionDialog = ({
  isOpen,
  onClose,
  onEditQuestion,
  question,
  categories,
}: EditQuestionDialogProps) => {
  const [formData, setFormData] = useState<Partial<Question>>(question);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData({
      ...question,
      correct_answer: Array.isArray(question.correct_answer)
        ? question.correct_answer
        : [question.correct_answer].filter(Boolean),
      question_images: question.question_images || [],
      explanation_images: question.explanation_images || [],
    });
  }, [question]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onEditQuestion(formData);
    setSaving(false);
    onClose();
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...(formData.options || [])];
    const oldValue = newOptions[index];
    newOptions[index] = value;

    // Keep correct_answer in sync: if the edited option was a correct answer,
    // update its text in the correct_answer array so it continues to match.
    const currentCorrectAnswers = formData.correct_answer || [];
    const newCorrectAnswers = currentCorrectAnswers.map(ans =>
      ans === oldValue ? value : ans
    );

    setFormData({ ...formData, options: newOptions, correct_answer: newCorrectAnswers });
  };

  const addOption = () => {
    setFormData({ ...formData, options: [...(formData.options || []), ''] });
  };

  const removeOption = (index: number) => {
    const removedOption = (formData.options || [])[index];
    const newOptions = (formData.options || []).filter((_, i) => i !== index);
    // Remove the deleted option from correct_answer if it was selected
    const newCorrectAnswers = (formData.correct_answer || []).filter(ans => ans !== removedOption);
    setFormData({ ...formData, options: newOptions, correct_answer: newCorrectAnswers });
  };

  const handleCorrectAnswerChange = (option: string) => {
    const currentAnswers = formData.correct_answer || [];
    const newAnswers = currentAnswers.includes(option)
      ? currentAnswers.filter((ans) => ans !== option)
      : [...currentAnswers, option];
    setFormData({ ...formData, correct_answer: newAnswers });
  };

  if (!question) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Question</DialogTitle>
          <DialogDescription>
            Update the question details below
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question_text">Question Text</Label>
            <Textarea
              id="question_text"
              value={formData.question_text || ''}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              placeholder="Enter the question text"
              required
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="question_type">Question Type</Label>
            <Select
              value={formData.question_type || ''}
              onValueChange={(value) => setFormData({ ...formData, question_type: value, options: value === 'multiple_choice' ? formData.options || [] : [] })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select question type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                <SelectItem value="true_false">True/False</SelectItem>
                <SelectItem value="essay">Essay</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.question_type === 'multiple_choice' && (
            <div className="space-y-2">
              <Label>Options</Label>
              {(formData.options || []).map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Option ${String.fromCharCode(65 + index)}`}
                    required
                  />
                  {(formData.options || []).length > 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeOption(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              {(formData.options || []).length < 6 && (
                <Button type="button" variant="outline" size="sm" onClick={addOption}>
                  Add Option
                </Button>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Correct Answer</Label>
            {formData.question_type === 'multiple_choice' ? (
              <div className="space-y-2 rounded-md border p-2">
                <p className="text-sm text-muted-foreground">Select one or more correct answers.</p>
                {(formData.options || []).map((option, index) => (
                  option && (
                    <div key={index} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-correct-answer-${index}`}
                        checked={(formData.correct_answer || []).includes(option)}
                        onCheckedChange={() => handleCorrectAnswerChange(option)}
                      />
                      <Label htmlFor={`edit-correct-answer-${index}`} className="font-normal">
                        {option}
                      </Label>
                    </div>
                  )
                ))}
              </div>
            ) : formData.question_type === 'true_false' ? (
              <Select
                value={Array.isArray(formData.correct_answer) ? formData.correct_answer[0] || '' : formData.correct_answer || ''}
                onValueChange={(value) => setFormData({ ...formData, correct_answer: [value] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select correct answer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True</SelectItem>
                  <SelectItem value="false">False</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="correct_answer"
                value={Array.isArray(formData.correct_answer) ? formData.correct_answer.join(', ') : formData.correct_answer || ''}
                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value.split(',').map(s => s.trim()) })}
                placeholder="Enter correct answer(s), comma-separated"
                required
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="explanation">Explanation (Optional)</Label>
            <Textarea
              id="explanation"
              value={formData.explanation || ''}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              placeholder="Provide an explanation for the answer"
              rows={2}
            />
          </div>

          <ImageUpload
            label="Question Images"
            images={formData.question_images || []}
            onImagesChange={(images) => setFormData({ ...formData, question_images: images })}
            maxImages={3}
          />

          <ImageUpload
            label="Explanation Images"
            images={formData.explanation_images || []}
            onImagesChange={(images) => setFormData({ ...formData, explanation_images: images })}
            maxImages={3}
          />
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};